import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../auth/AuthProvider";
import { C } from "../customers/theme";
import AtlasRobot from "./AtlasRobot";
import AtlasChatPanel from "./AtlasChatPanel";

// AtlasAssistant.jsx (P4-CC12) — mounted ONCE, unconditionally, in the
// authenticated part of MsquaredApp's render tree, immediately after
// `if (!user) return <AuthScreen />;`. That guard is the entire hiding
// mechanism — no route logic exists in this app, so nothing else is needed
// for the assistant to be invisible pre-sign-in.
//
// Z-INDEX: this whole surface (avatar/bubble/panel, 50/51/55) sits BELOW the
// app's existing modal layer (100) on purpose. Every modal in this app renders
// a full-viewport, pointer-capturing scrim at z:100 (S.modal, theme.js +
// MsquaredApp.jsx), so placing the assistant below it means any open modal
// STRUCTURALLY hides and disables the assistant for exactly as long as it's
// open, then restores it instantly on close — not by luck, and with no
// hand-rolled "is a modal open" flag to keep in sync. Existing layers (mobile
// drawer scrim 199, mobile sidebar 200, "Saved" toast 300) are untouched; the
// toast collision is resolved by offsetting THIS component upward, not by
// moving the toast.
const Z_AVATAR = 50;
const Z_BUBBLE = 51;
// P4-CC23-B(2b.2) — 10s, not the original 6s. The greeting text is now the ~112-char
// EU AI Act disclosure (~20 words); at ordinary reading pace that's close to 6s just
// to read it once, before allowing any time to notice the bubble at all. For a user
// who never opens the panel, this bubble is the ONLY Article 50 disclosure they ever
// see, so its lifetime is coupled to the length of that text — if the greeting copy
// changes materially, re-check this value against it.
const GREETING_MS = 10000;
const AVATAR_BOTTOM = "calc(84px + env(safe-area-inset-bottom, 0px))";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", onChange); else mq.removeListener(onChange); };
  }, []);
  return reduced;
}

export default function AtlasAssistant() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const avatarRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  // Greeting — split into two effects on purpose (a single combined
  // effect breaks under React 18 StrictMode's dev-only run->cleanup->run: the
  // first run sets the flag + starts a timer, the cleanup clears that timer,
  // and the second run sees the flag already set and returns early with NO
  // new timer — so the bubble would never auto-dismiss in `npm start`,
  // although production and this file's own RTL tests, which don't opt into
  // StrictMode, would never catch it).
  //
  // Effect A — DECISION ONLY, depends on [user?.id]. Fires once per
  // (browser-tab session x signed-in user): keyed by user id so a DIFFERENT
  // person signing in on a shared machine/tab is still greeted, while the
  // SAME user signing out and back in in the same tab is not. user?.id is
  // stable across a Supabase token refresh (AuthProvider's onAuthChange only
  // updates session/user object references) and across screen navigation
  // (this component never unmounts on those). No timer, no cleanup here — its
  // StrictMode double-invoke is harmless because the second run just reads
  // the flag the first run already wrote and no-ops.
  useEffect(() => {
    if (!user?.id) return;
    const key = `atlas_greeted:${user.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { return; /* sessionStorage unavailable (e.g. privacy mode) — skip the greeting */ }
    setShowGreeting(true);
  }, [user?.id]);

  // Effect B — TIMER ONLY, depends on [showGreeting]. Re-created every time
  // showGreeting flips true — including the render triggered by Effect A's
  // setShowGreeting(true), which is a normal single-invoke dependency-driven
  // effect run, not part of the mount-time double-invoke dance — so the timer
  // always exists whenever the bubble is actually visible.
  useEffect(() => {
    if (!showGreeting) return;
    const t = setTimeout(() => setShowGreeting(false), GREETING_MS);
    return () => clearTimeout(t);
  }, [showGreeting]);

  const dismissGreeting = useCallback(() => setShowGreeting(false), []);

  useEffect(() => {
    if (!showGreeting) return;
    const onKey = (e) => { if (e.key === "Escape") dismissGreeting(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showGreeting, dismissGreeting]);

  const toggleOpen = () => { setShowGreeting(false); setOpen((o) => !o); };
  const close = useCallback(() => {
    setOpen(false);
    if (avatarRef.current) avatarRef.current.focus();
  }, []);

  return (
    <>
      {showGreeting && (
        <div
          role="status"
          onClick={dismissGreeting}
          style={{
            position: "fixed", bottom: `calc(${AVATAR_BOTTOM} + 60px)`, right: 20, zIndex: Z_BUBBLE,
            maxWidth: 240, background: C.surface2, color: C.text, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: "10px 14px", fontSize: 13, lineHeight: 1.4,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)", cursor: "pointer",
          }}
        >
          {/* EU AI Act Art 50(1) disclosure (P4-CC23-B) — exact required string.
              The old text interpolated companyName ("welcome to Atlas for {company}").
              Dropped on purpose: the required legal sentence alone is already
              ~110 chars / ~4 lines at this bubble's 240px maxWidth (13px font) —
              past the "about three lines" bound before any personalisation is
              added. The legal string wins; do not reintroduce companyName here. */}
          👋 Hi — I'm Atlas, an AI assistant from Msquared Automation Limited. Tap me any time you need help using the app.
        </div>
      )}
      <button
        ref={avatarRef}
        type="button"
        onClick={toggleOpen}
        aria-label={open ? "Close Atlas assistant" : "Open Atlas assistant"}
        aria-expanded={open}
        style={{
          position: "fixed", bottom: AVATAR_BOTTOM, right: 20, zIndex: Z_AVATAR,
          width: 52, height: 52, borderRadius: "50%", border: "none", padding: 0,
          background: "transparent", cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          boxShadow: "0 6px 20px rgba(0,182,214,0.35)",
        }}
      >
        <AtlasRobot size={52} isAnimating={!open && !reducedMotion} />
      </button>
      {open && <AtlasChatPanel onClose={close} />}
    </>
  );
}
